import { AppState, User } from "@auth0/auth0-react";
import { useCallback, useEffect } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import {
  getLoginRedirectSearchParamsFromStorage,
  mergeLoginRedirectSearchParamsFromLocation,
  stripAuthSearchParams,
  useLoginRedirectPath,
} from "shared/hooks";
import { useLastLogin } from "shared/hooks/useLastLogin";

const toUrlSearchParams = (search: string) =>
  new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);

const getDeepLinkParams = (...sources: (URLSearchParams | null | undefined)[]) => {
  const merged = new URLSearchParams();
  for (const source of sources) {
    if (!source) continue;
    source.forEach((value, key) => {
      if (key === "broadcast" || key === "resource" || key === "organizationId" || key === "editProfile") {
        merged.set(key, value);
      }
    });
  }
  return merged;
};

export const useAuth0WithNavigate = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { setLastLogin } = useLastLogin();
  const {
    clearLoginRedirectPath,
    getLoginRedirectPath,
    setLoginRedirectPath,
    getLoginRedirectSearchParams,
    clearLoginRedirectSearchParams,
  } = useLoginRedirectPath();

  // Set the login redirect path
  useEffect(() => {
    if (!getLoginRedirectPath() && location.pathname !== "/app") {
      setLoginRedirectPath(location.pathname);
    }
  }, [getLoginRedirectPath, location.pathname, setLoginRedirectPath]);

  // Persist deep-link params (e.g. broadcast) from the current URL
  useEffect(() => {
    if (!location.search || location.search.length <= 1) return;
    if (location.search.includes("code")) return;

    mergeLoginRedirectSearchParamsFromLocation(location.search);
  }, [location.search]);

  // Restore stored deep-link params after password reset or Auth0 flows that land on /app bare
  useEffect(() => {
    const storedSearchParams = getLoginRedirectSearchParamsFromStorage();
    if (!storedSearchParams || !location.pathname.startsWith("/app")) {
      return;
    }

    const storedParams = toUrlSearchParams(storedSearchParams);
    const currentParams = stripAuthSearchParams(
      new URLSearchParams(location.search),
    );
    const merged = getDeepLinkParams(storedParams, currentParams);

    if (!merged.toString()) return;
    if (merged.toString() === currentParams.toString()) return;

    setSearchParams(merged, { replace: true });
  }, [location.pathname, location.search, setSearchParams]);

  /**
   * The redirect callback from Auth0
   * - When a user logs in, we need to create an Activity log item for the user
   */
  const redirectCallback = useCallback(
    (appState?: AppState, user?: User) => {
      if (user) {
        setLastLogin(user.updated_at ?? new Date().toISOString());
      }

      const returnPath = getLoginRedirectPath();
      const returnSearchParams = getLoginRedirectSearchParams();
      const storedParams = returnSearchParams
        ? toUrlSearchParams(returnSearchParams)
        : null;

      // Auth0 appState.returnTo is the most reliable carrier through the login round-trip
      const appStateReturnTo =
        appState && typeof appState === "object" && "returnTo" in appState
          ? String(appState.returnTo ?? "")
          : "";
      const appStateParams = appStateReturnTo
        ? stripAuthSearchParams(
            toUrlSearchParams(new URL(appStateReturnTo, window.location.origin).search),
          )
        : null;

      const restoredParams = getDeepLinkParams(appStateParams, storedParams);

      if (returnPath) {
        clearLoginRedirectPath();
        navigate({
          pathname: returnPath,
          search: restoredParams.toString(),
        });
      } else if (appStateReturnTo) {
        const returnUrl = new URL(appStateReturnTo, window.location.origin);
        navigate(
          {
            pathname: returnUrl.pathname || "/app",
            search: restoredParams.toString(),
          },
          { replace: true },
        );
      } else if (restoredParams.toString()) {
        setSearchParams(restoredParams, { replace: true });
      } else {
        setSearchParams(stripAuthSearchParams(new URLSearchParams(location.search)), {
          replace: true,
        });
      }

      if (returnSearchParams) {
        setTimeout(() => {
          clearLoginRedirectSearchParams();
        }, 100);
      }
    },
    [
      clearLoginRedirectPath,
      clearLoginRedirectSearchParams,
      getLoginRedirectPath,
      getLoginRedirectSearchParams,
      location.search,
      navigate,
      setLastLogin,
      setSearchParams,
    ],
  );

  return {
    redirectCallback,
    searchParams,
  };
};
